import { Vec3 } from '../math/vec3';
import { Quat } from '../math/quat';
import { Mat4 } from '../math/mat4';

const _p = new Vec3(), _s = new Vec3(), _q = new Quat();

/**
 * Position / rotation / scale with parent links. Keeps the previous sim state so the
 * renderer can interpolate between fixed steps. `world` is the matrix used for rendering.
 */
export class Transform {
  position = new Vec3();
  rotation = new Quat();
  scale = new Vec3(1, 1, 1);
  prevPosition = new Vec3();
  prevRotation = new Quat();
  prevScale = new Vec3(1, 1, 1);
  parent: Transform | null = null;
  readonly children: Transform[] = [];
  readonly local = new Mat4();
  readonly world = new Mat4();
  /** Non-interpolated world matrix from the latest sim state (for physics queries). */
  readonly worldNow = new Mat4();
  /** Owning entity id, set by the game when the transform is registered. */
  entity = -1;
  /** Set false to hide this and all children from rendering. */
  visible = true;

  setParent(p: Transform | null): this {
    if (this.parent) {
      const i = this.parent.children.indexOf(this);
      if (i >= 0) this.parent.children.splice(i, 1);
    }
    this.parent = p;
    if (p) p.children.push(this);
    return this;
  }

  /** Copy current state into prev; call once per sim step before mutating. */
  snapPrev(): void {
    this.prevPosition.copy(this.position);
    this.prevRotation.copy(this.rotation);
    this.prevScale.copy(this.scale);
  }

  /** Move without leaving an interpolation streak. */
  teleport(p: Vec3): void {
    this.position.copy(p);
    this.prevPosition.copy(p);
  }

  /** Compute local + world matrices at interpolation alpha, recursing into children. */
  updateWorld(alpha: number, parentWorld?: Mat4): void {
    if (alpha >= 1) {
      this.local.compose(this.position, this.rotation, this.scale);
    } else {
      _p.copy(this.prevPosition).lerp(this.position, alpha);
      _q.copy(this.prevRotation).slerp(this.rotation, alpha);
      _s.copy(this.prevScale).lerp(this.scale, alpha);
      this.local.compose(_p, _q, _s);
    }
    if (parentWorld) this.world.multiplyMatrices(parentWorld, this.local);
    else this.world.copy(this.local);
    for (const c of this.children) c.updateWorld(alpha, this.world);
  }

  /** Compute worldNow from current (non-interpolated) state. */
  updateWorldNow(parentWorld?: Mat4): void {
    const m = _tmpLocal.compose(this.position, this.rotation, this.scale);
    if (parentWorld) this.worldNow.multiplyMatrices(parentWorld, m);
    else this.worldNow.copy(m);
    for (const c of this.children) c.updateWorldNow(this.worldNow);
  }

  /** Recompute worldNow for this transform only, walking up the parent chain (no child recursion). */
  computeWorldNow(): Mat4 {
    let parentM: Mat4 | undefined;
    if (this.parent) parentM = this.parent.computeWorldNow();
    const m = _tmpLocal.compose(this.position, this.rotation, this.scale);
    if (parentM) this.worldNow.multiplyMatrices(parentM, m); else this.worldNow.copy(m);
    return this.worldNow;
  }

  getWorldPosition(out = new Vec3()): Vec3 {
    return this.computeWorldNow().getTranslation(out);
  }

  /** Forward direction (+Z rotated by rotation). */
  forward(out = new Vec3()): Vec3 { return this.rotation.rotate(out.set(0, 0, 1)); }
  right(out = new Vec3()): Vec3 { return this.rotation.rotate(out.set(1, 0, 0)); }

  lookYaw(yaw: number): this { this.rotation.setYaw(yaw); return this; }
  get yaw(): number { return this.rotation.getYaw(); }
}
const _tmpLocal = new Mat4();
